<template>
  <aside class="rail">
    <h3 class="rail-head">
      <span class="lbl-mono">Sprint history</span>
      <span v-if="sprints.length" class="rail-count">{{ sprints.length }}</span>
    </h3>

    <ul v-if="sprints.length" class="rail-list">
      <li v-for="(s, i) in sprints" :key="s.sprint_id">
        <RouterLink
          :to="`/p/${projectKey}/s/${s.sprint_id}`"
          :class="['mini', { current: s.sprint_id === currentSprintId }]"
        >
          <span :class="['rag-dot', ragFor(s)]" aria-hidden="true" />
          <div class="mini-body">
            <div class="mini-head">
              <span class="mini-name">{{ s.sprint_name }}</span>
              <span v-if="s.sprint_state === 'active'" class="state-pill live">
                <span class="dot" aria-hidden="true" />active
              </span>
              <span v-else-if="i === 0" class="state-pill latest">latest</span>
            </div>
            <div class="mini-meta">
              <span class="mono">{{ fmtRange(s.sprint_start, s.sprint_end) }}</span>
              <span v-if="completion(s) !== null" :class="['mini-pct', ragFor(s)]">
                {{ Math.round((completion(s) ?? 0) * 100) }}%
              </span>
            </div>
          </div>
        </RouterLink>
      </li>
    </ul>

    <p v-else class="rail-empty">No prior sprints.</p>
  </aside>
</template>

<script setup lang="ts">
import { RouterLink } from "vue-router";
import type { RagBand, SprintListItem } from "@/api/types";
import { fmtDate } from "@/lib/format";

const props = defineProps<{
  projectKey: string;
  sprints: SprintListItem[];
  /** Highlight the sprint currently focused in the center column. */
  currentSprintId?: number;
}>();

function completion(s: SprintListItem): number | null {
  const env = s.headline_metrics?.M7;
  if (!env) return null;
  const v = env.value;
  return typeof v === "number" ? v : null;
}

function ragFor(s: SprintListItem): RagBand {
  const m7 = s.headline_metrics?.M7?.rag;
  if (m7 && m7 !== "none") return m7;
  if (s.low_signal) return "red";
  return "none";
}

function fmtRange(start: string, end: string): string {
  return `${fmtDate(start)} → ${fmtDate(end)}`;
}

// Re-export for template type-narrowing without unused-var lint noise.
defineExpose({ projectKey: props.projectKey });
</script>

<style scoped>
.rail { display: grid; gap: 14px; align-content: start; }

.rail-head {
  display: flex; align-items: center; justify-content: space-between;
  margin: 0 0 4px;
  font-weight: 500;
}
.rail-head .lbl-mono { color: var(--muted); }
.rail-count {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px;
  color: var(--muted-2);
  background: var(--bg-tint);
  padding: 1px 8px;
  border-radius: 999px;
}

.rail-list {
  list-style: none;
  padding: 0; margin: 0;
  display: grid; gap: 8px;
}
.rail-empty {
  color: var(--muted);
  font-size: 13px;
  margin: 0;
  padding: 12px;
  border: 1px dashed var(--line);
  border-radius: 12px;
  text-align: center;
}

.mini {
  display: grid; grid-template-columns: 4px 1fr;
  gap: 12px; align-items: stretch;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--bg-elev);
  color: inherit;
  cursor: pointer;
  transition: border-color var(--speed-quick) var(--ease),
              background var(--speed-quick) var(--ease),
              transform var(--speed-quick) var(--ease);
}
.mini:hover {
  border-color: var(--ink);
  transform: translateY(-1px);
}
.mini:focus-visible {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 18%, transparent);
}
.mini.current {
  border-color: color-mix(in srgb, var(--brand) 50%, transparent);
  background: var(--brand-tint);
}

.rag-dot {
  width: 4px; border-radius: 4px;
  background: var(--muted-2);
}
.rag-dot.red    { background: var(--red); }
.rag-dot.amber  { background: var(--amber); }
.rag-dot.green  { background: var(--green); }
.rag-dot.none   { background: var(--muted-2); }

.mini-body { display: grid; gap: 6px; min-width: 0; }
.mini-head {
  display: flex; align-items: center;
  gap: 8px; flex-wrap: wrap;
}
.mini-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  letter-spacing: -0.01em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 100%;
}

.state-pill {
  font-family: "JetBrains Mono", monospace;
  font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase;
  padding: 1px 6px;
  border-radius: 999px;
}
.state-pill.live {
  background: var(--green-soft);
  color: var(--green-ink);
  display: inline-flex; align-items: center; gap: 4px;
}
.state-pill.live .dot {
  width: 5px; height: 5px;
  border-radius: 50%;
  background: var(--green);
  animation: pulse 1.6s ease-in-out infinite;
}
@keyframes pulse { 50% { opacity: 0.3; } }
.state-pill.latest {
  background: var(--brand-soft);
  color: var(--brand);
}

.mini-meta {
  display: flex; align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  color: var(--muted);
}
.mini-meta .mono {
  font-family: "JetBrains Mono", monospace;
  letter-spacing: 0.04em;
}

.mini-pct {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 4px;
}
.mini-pct.red    { background: var(--red-soft);   color: var(--red-ink); }
.mini-pct.amber  { background: var(--amber-soft); color: var(--amber-ink); }
.mini-pct.green  { background: var(--green-soft); color: var(--green-ink); }
.mini-pct.none   { background: var(--bg-tint);    color: var(--muted); }
</style>
